import classnames from 'classnames';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDomainConfiguration } from '@/components/DomainConfigurationProvider';

import {
    PageLocale,
    TopBannerConfiguration
} from '@cineamo/api/models/premium-movie-landing-page-configuration/PremiumMovieLandingPageConfigurationDto';

import { ID_ALS_WAERE_ES_LEICHT } from '@cineamo/api/constants/movie-ids';

import { copyToClipboard } from '@cineamo/ui/src/helpers/clipboard-helper';

import Button from '@cineamo/ui/src/button/Button';
import { IconMenu24 } from '@cineamo/ui/src/icons/ic-menu';
import { IconPlay16 } from '@cineamo/ui/src/icons/ic-play';
import { IconPlayVideo16 } from '@cineamo/ui/src/icons/ic-playVideo';
import { IconShareBox24 } from '@cineamo/ui/src/icons/ic-share-box';
import { IconTicket16 } from '@cineamo/ui/src/icons/ic-ticket';

import CenterLayout from '@cineamo/composables/src/CenterLayout';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { HOME_ENTERTAINMENT_ANCHOR_ID, useHomeEntertainment } from './home-entertainment/useHomeEntertainment';
import PageMenu from './PageMenu';
import ShowtimesFinderMenu from './showtimes-finder/ShowtimesFinderMenu';

const Banner: React.FC<TopBannerConfiguration> = ({
    backgroundColor = '#FFFFFF',
    textColor = '#000000',
    text,
    logo,
    logoHref
}) => {
    const locale = useLocale() as PageLocale;

    return (
        <div
            className="w-full z-[1]"
            style={{
                backgroundColor: backgroundColor,
                color: textColor
            }}>
            <CenterLayout className="flex min-h-48 px-9 py-9 lg:py-12 items-center justify-center">
                <div className="relative flex flex-col lg:flex-row items-center w-full h-full space-y-16 lg:space-y-0">
                    {logo && (
                        <a
                            className="w-auto h-full"
                            href={logoHref}
                            target="_blank"
                            rel="noreferrer">
                            <Image
                                className="w-auto h-40 object-contain"
                                src={logo}
                                width={200}
                                height={40}
                                alt="Distributor Logo"
                            />
                        </a>
                    )}
                    {text?.[locale] && (
                        <div className="lg:absolute left-0 top-0 flex flex-row w-full h-full items-center justify-center">
                            <span
                                className="text-center whitespace-pre-wrap text-body-bold-12 lg:text-body-bold-14"
                                dangerouslySetInnerHTML={{
                                    __html: text[locale]
                                }}
                            />
                        </div>
                    )}
                </div>
            </CenterLayout>
        </div>
    );
};

const cssPropertiesObjectToString = (style: CSSProperties): string =>
    Object.entries(style)
        .map(([property, value]) => `${property.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${String(value)}`)
        .join('; ');

const generateResponsiveCSS = (selector: string, desktopCss: string, mobileCss: string): string => {
    let css = '';
    if (desktopCss) {
        css += `${selector} { ${desktopCss} } `;
    }
    if (mobileCss) {
        css += `@media (max-width: 1024px) { ${selector} { ${mobileCss} } }`;
    }
    return css;
};

const PageHeroContent: React.FC<{
    onWatchTrailerClick: () => void;
    onPrimaryCtaClick: () => void;
    onShareClick: () => void;
    onMenuClick: () => void;
}> = ({ onWatchTrailerClick, onPrimaryCtaClick, onShareClick, onMenuClick }) => {
    const t = useTranslations();
    const { domainConfiguration, movie } = useDomainConfiguration();
    const { isActive: isHomeEntertainment, buttonLabel: homeEntertainmentLabel } = useHomeEntertainment();
    const heroImage = domainConfiguration.heroBackdropUrl;
    const movieTitle = movie.title;

    const heroStyleTag = useMemo(() => {
        const legacyCss = domainConfiguration.heroContentStyle
            ? cssPropertiesObjectToString(domainConfiguration.heroContentStyle)
            : '';

        const contentDesktop = [legacyCss, domainConfiguration.heroContentStyleList ?? ''].filter(Boolean).join('; ');

        const css =
            generateResponsiveCSS(
                '[data-hero-content]',
                contentDesktop,
                domainConfiguration.heroContentStyleListMobile ?? ''
            ) +
            generateResponsiveCSS(
                '[data-hero-buttons]',
                domainConfiguration.heroButtonsStyleList ?? '',
                domainConfiguration.heroButtonsStyleListMobile ?? ''
            );

        return css;
    }, [domainConfiguration]);

    return (
        <div className="relative w-full h-full flex flex-col">
            {heroStyleTag && <style dangerouslySetInnerHTML={{ __html: heroStyleTag }} />}
            {/* Distributor or Custom Text Banner */}
            {domainConfiguration.topBanner && <Banner {...domainConfiguration.topBanner} />}
            <div className="relative w-full h-full">
                <div className="absolute left-0 top-0 w-full h-full flex flex-col">
                    {/* {domainConfiguration.premiumMovieVideoUrl && !heroImage && (
                            <ReactPlayer
                                className=" -z-10 w-full h-full pointer-events-none"
                                width="100%"
                                height="100%"
                                playing={true}
                                config={{
                                    youtube: {
                                        playerVars: {
                                            showInfo: 0,
                                            modestbranding: 0,
                                            rel: 0
                                        }
                                    }
                                }}
                                playIcon={null}
                                controls={false}
                                muted
                                playsinline
                                loop
                                url={domainConfiguration.premiumMovieVideoUrl}
                            />
                        )} */}

                    {heroImage && (
                        <Image
                            className="w-full h-full object-cover"
                            src={heroImage}
                            width={10000}
                            height={10000}
                            alt={`${movieTitle} Backdrop`}
                        />
                    )}
                </div>
                {![ID_ALS_WAERE_ES_LEICHT].includes(domainConfiguration.movieId) && (
                    <div className="absolute left-0 top-0 w-full h-full bg-gradient-to-b from-black/20 to-black/0" />
                )}

                <div className="relative w-full h-full flex flex-col items-center">
                    <div
                        className="px-16 py-9 w-full flex flex-row justify-between"
                        style={
                            ID_ALS_WAERE_ES_LEICHT === domainConfiguration.movieId
                                ? {
                                      color: '#565656'
                                  }
                                : {}
                        }>
                        <button
                            className="p-12 cursor-pointer"
                            onClick={onMenuClick}
                            aria-label={t('common.menu')}>
                            <IconMenu24 aria-hidden="true" />
                        </button>
                        <button
                            className="p-12 cursor-pointer"
                            onClick={() => onShareClick()}
                            aria-label={t('common.share')}>
                            <IconShareBox24 aria-hidden="true" />
                        </button>
                    </div>
                    <div
                        className="my-auto flex flex-col space-y-48 items-center"
                        data-hero-content>
                        {domainConfiguration.titleArtworkUrl && (
                            <div
                                className={classnames(
                                    'w-[80vw] max-h-[220px] h-auto',
                                    domainConfiguration.titleArtworkOrientation === 'portrait'
                                        ? 'max-w-[416px]'
                                        : 'max-w-[740px]'
                                )}>
                                <Image
                                    className="w-full h-full object-contain"
                                    src={domainConfiguration.titleArtworkUrl}
                                    width={10000}
                                    height={10000}
                                    alt={`${movieTitle}`}
                                />
                            </div>
                        )}
                        <div
                            className="flex flex-col lg:flex-row gap-24 px-72 lg:px-0 w-full lg:w-auto"
                            data-hero-buttons>
                            <Button
                                className="!rounded-8 w-full lg:w-auto h-48"
                                style={{
                                    backgroundColor: domainConfiguration.style.primaryColor,
                                    color: domainConfiguration.style.onPrimaryColor
                                }}
                                onClick={onWatchTrailerClick}
                                leadingIcon={<IconPlay16 className="w-24 h-24 flex-none" />}>
                                {t('common.watch-trailer')}
                            </Button>
                            <Button
                                className="!rounded-8 w-full lg:w-auto h-48"
                                style={{
                                    backgroundColor: domainConfiguration.style.primaryColor,
                                    color: domainConfiguration.style.onPrimaryColor
                                }}
                                onClick={onPrimaryCtaClick}
                                leadingIcon={
                                    isHomeEntertainment ? (
                                        <IconPlayVideo16 className="w-24 h-24 flex-none" />
                                    ) : (
                                        <IconTicket16 className="w-24 h-24 flex-none" />
                                    )
                                }>
                                {isHomeEntertainment ? homeEntertainmentLabel : t('common.find-tickets')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const PageHeader: React.FC<{
    onWatchTrailerClick: () => void;
    onPlotClick: () => void;
    onInfoClick: () => void;
}> = ({ onWatchTrailerClick, onPlotClick, onInfoClick }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isShowtimesAndTicketsOpen, setIsShowtimesAndTicketsOpen] = useState(false);
    const t = useTranslations();
    const { track } = useAnalytics();
    const { isActive: isHomeEntertainment } = useHomeEntertainment();
    const headerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // A hash in the URL is a deliberate deep link (e.g. /gewinnspiel redirects to /#contest).
        // The browser has already jumped to that section, so pulling the hero back into view here
        // would yank the viewport to the top before ScrollToAnchor scrolls back down again.
        if (window.location.hash) return;
        headerRef.current?.scrollIntoView(false);
    }, []);

    const sharePageLink = useCallback(async () => {
        const link = window.location.href;
        const shareData = {
            url: link
        };
        if (!navigator.canShare?.(shareData)) {
            copyToClipboard(link, t('showing-info.copied'));
            return;
        }
        track('Page Shared');
        await navigator.share(shareData).catch(() => {});
    }, [t, track]);

    const openShowtimesAndTickets = useCallback(() => {
        setIsShowtimesAndTicketsOpen(true);
        track('Page Showtimes and Tickets Opened');
    }, [track]);

    // In home-entertainment mode the hero's ticket CTA becomes the offers CTA: instead of opening
    // the showtimes finder it jumps to the offers section further down the page.
    const onPrimaryCtaClick = useCallback(() => {
        if (!isHomeEntertainment) {
            openShowtimesAndTickets();
            return;
        }
        track('Page Home Entertainment Clicked');
        document.getElementById(HOME_ENTERTAINMENT_ANCHOR_ID)?.scrollIntoView({
            block: 'start',
            behavior: 'smooth'
        });
    }, [isHomeEntertainment, openShowtimesAndTickets, track]);

    const openMenu = useCallback(() => {
        setIsMenuOpen(true);
        track('Page Menu Opened');
    }, [track]);

    return (
        <div
            className="relative w-full min-h-[100dvh] h-[100dvh] lg:h-[100dvh] flex flex-col"
            ref={headerRef}>
            <PageMenu
                isMenuOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                onPrimaryCtaClick={onPrimaryCtaClick}
                onWatchTrailerClick={onWatchTrailerClick}
                onPlotClick={onPlotClick}
                onInfoClick={onInfoClick}
                onShareClick={sharePageLink}
            />
            {!isHomeEntertainment && (
                <ShowtimesFinderMenu
                    isMenuOpen={isShowtimesAndTicketsOpen}
                    onMenuClick={openMenu}
                    onClose={() => setIsShowtimesAndTicketsOpen(false)}
                    onShareClick={sharePageLink}
                />
            )}
            <PageHeroContent
                onWatchTrailerClick={onWatchTrailerClick}
                onPrimaryCtaClick={onPrimaryCtaClick}
                onShareClick={sharePageLink}
                onMenuClick={openMenu}
            />
        </div>
    );
};

export default PageHeader;
